import { AIService } from "./AIService";
import { Message, Settings, TagSet, CorpSet } from "./types";
import { GameStreamingService } from './GameManager';

export class ProcessorService implements AIService {
    // Method to handle button actions from chat messages
    async handleButtonAction(action: string, buttonId: string): Promise<any> {
        // Implement API call to backend to handle the button action
        console.log(`Processing button action: ${action}, buttonId: ${buttonId}`);
        try {
            const response = await fetch(`${this.apiUrl}/api/button-action`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${this.token}`
                },
                body: JSON.stringify({
                    action,
                    buttonId
                })
            });

            if (!response.ok) {
                throw new Error(`Failed to process button action: ${response.statusText}`);
            }

            return await response.json();
        } catch (error) {
            console.error('Failed to process button action:', error);
            throw error;
        }
    }
    private static configPromise: Promise<any> | null = null;
    public apiUrl: string;
    public token: string;
    private settings: Settings;
    private gameService: GameStreamingService;
    private automab = false;

    constructor(token: string, gameService: GameStreamingService, automab = false) {
        this.apiUrl = "not initialized";
        this.token = token;
        this.settings = { openAiApiKey: "", chatModel: "gpt-4o-mini", realtimeModel: "gpt-4o-realtime-preview-2024-10-01" };
        this.gameService = gameService;
        this.automab = automab;
    }

    async init() {
        await this.loadConfig();
    }

    private static async getConfig() {
        if (!this.configPromise) {
            this.configPromise = fetch('/devconfig.json').then(response => response.json());
        }
        return this.configPromise;
    }

    public static async getHttpUrl(isAutomab = false): Promise<string> {
        const pathname = isAutomab ? '/automab' : '/api';
        const hostname = window.location.hostname;
        try {
            if (hostname === "localhost" || hostname === "127.0.0.1" || hostname === "::1") {
                return `${window.location.protocol}//${hostname}:3003/api`;
            } else {
                return `${window.location.origin}${pathname}`;
            }
        } catch (error) {
            console.error('Failed to load config:', error);
            return pathname;
        }
    }

    private async loadConfig() {
        this.apiUrl = await ProcessorService.getHttpUrl(this.automab);
        console.log('API URL set to:', this.apiUrl);
    }

    private async apiCall<T>(route: string, data: any = {}, method = 'POST'): Promise<T> {
        try {
            let url = `${this.apiUrl}/${route}`;
            console.log(`[ProcessorService] apiCall ${route} with token (length: ${this.token.length})`);
            console.log(`[ProcessorService] Full URL: ${url}`);

            const options: RequestInit = {
                method: method,
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${this.token}`
                },
            };

            if (method !== 'GET') {
                // Create a safe copy of data without sensitive information
                const safeData = { ...data };
                if (safeData.token) safeData.token = '[REDACTED]';
                if (safeData.settings && safeData.settings.openAiApiKey) {
                    safeData.settings = { ...safeData.settings, openAiApiKey: '[REDACTED]' };
                }
                options.body = JSON.stringify({ ...data, token: this.token });
                console.log(`[ProcessorService] Request body (sanitized):`, JSON.stringify(safeData));
            } else {
                // For GET requests, append token as a query parameter
                url += (url.includes('?') ? '&' : '?') + `token=${encodeURIComponent(this.token)}`;
                console.log(`[ProcessorService] Final URL with token (sanitized): ${url.replace(this.token, '[REDACTED]')}`);
            }

            console.log(`[ProcessorService] Sending ${method} request to ${url.replace(this.token, '[REDACTED]')}`);
            const response = await fetch(url, options);
            console.log(`[ProcessorService] Response status: ${response.status} ${response.statusText}`);

            if (!response.ok) {
                let errorData = await response.json();
                console.log('URL', url.replace(this.token, '[REDACTED]'), 'FULL errorData', errorData);
                while (errorData.error) {
                    errorData = errorData.error;
                }
                if (typeof errorData === 'string') {
                    errorData = { message: errorData };
                }
                throw new Error(errorData.message || 'An error occurred');
            }

            const responseData = await response.json();
            console.log(`[ProcessorService] Response data for ${route}:`, responseData);
            return responseData;
        } catch (error) {
            console.error(`Error in ${route} API call:`, error);
            throw error;
        }
    }
    public setSettings(settings: Settings): void {
        this.settings = settings;
    }

    public startSpeechRecognition(): void {
        console.error("Speech api is not implemented.");
    }

    public stopSpeechRecognition(): void {
        console.error("Speech api is not implemented.");
    }

    public speakText(text: string): void {
        console.error("Speech api is not implemented.", text);
    }

    public stopSpeaking(): void {
        console.error("Speech api is not implemented.");
    }

    public async chat(prompt: string): Promise<string> {
        return this.generateResponse(prompt, "processor")
    }

    public async generateResponse(prompt: string, route: string): Promise<string> {
        const data = await this.apiCall<{ message: string }>(route, { prompt: prompt, settings: this.settings });
        return data.message;
    }

    public async triggerSystem2(): Promise<string> {
        const data = await this.apiCall<{ message: string }>("system2", { settings: this.settings });
        return data.message;
    }

    public async getAllMessages(): Promise<Message[]> {
        return await this.apiCall<Message[]>("messages");
    }

    public async resetChat(): Promise<void> {
        const resetAck = await this.gameService.sendAndWaitForResponse();
        console.log('resetAck rxd', resetAck);
    }

    public async getTags(): Promise<{ tagSet: TagSet[] }> {
        return await this.apiCall<{ tagSet: TagSet[] }>("get_tags");
    }

    public async setTags(tagSet: TagSet[]): Promise<void> {
        await this.apiCall<{ message: string }>("set_tags", { tagSet });
    }

    public async getDefaultTags(): Promise<{ tagSet: TagSet[] }> {
        return await this.apiCall<{ tagSet: TagSet[] }>("get_default_tags");
    }

    public async getCorporateAI(): Promise<{ corporateAI: CorpSet }> {
        return await this.apiCall<{ corporateAI: CorpSet }>("get_corporate_ai");
    }

    public async getProcessModelXml(): Promise<{ processModelXml: string }> {
        const response = await this.apiCall<{ processModelXml: string }>("get_process_model_xml");
        console.log('[ProcessorService] getProcessModelXml response received (XML length:', response.processModelXml?.length || 0, ')');

        // Check if the XML is valid
        if (response.processModelXml) {
            // Check if the XML contains a diagram element
            if (!response.processModelXml.includes('<bpmndi:BPMNDiagram')) {
                console.warn('[ProcessorService] XML does not contain a diagram element (<bpmndi:BPMNDiagram>)');
            }
            // Check if the XML contains a process element
            if (!response.processModelXml.includes('<bpmn:process')) {
                console.warn('[ProcessorService] XML does not contain a process element (<bpmn:process>)');
            }
            // Log a small preview of the XML
            console.log('[ProcessorService] XML preview:', response.processModelXml.substring(0, 100) + (response.processModelXml.length > 100 ? '...' : ''));
        } else {
            console.warn('[ProcessorService] processModelXml is null or undefined');
        }

        return response;
    }

    public async getDefaultProcessModelXml(): Promise<{ processModelXml: string }> {
        return await this.apiCall<{ processModelXml: string }>("get_default_process_model_xml");
    }

    public async setCorporateAI(corporateAI: CorpSet): Promise<void> {
        await this.apiCall<{ message: string }>("set_corporate_ai", { corporateAI });
    }

    public async setProcessModelXml(processModelXml: string): Promise<void> {
        await this.apiCall<{ message: string }>("set_process_model_xml", { processModelXml });
    }

    public async getDefaultCorporateAI(): Promise<{ corporateAI: CorpSet }> {
        return await this.apiCall<{ corporateAI: CorpSet }>("get_default_corporate_ai");
    }

    public async getUserFiles(): Promise<{ files: { id: string; name: string }[] }> {
        return await this.apiCall<{ files: { id: string; name: string }[] }>("user-files", {}, 'GET');
    }

    public async getFileContent(fileId: string): Promise<{ content: string }> {
        return await this.apiCall<{ content: string }>(`user-files/${fileId}/content`, {}, 'GET');
    }

    public async addUserFile(name: string): Promise<void> {
        await this.apiCall<{ message: string }>("user-files", { name }, 'POST');
    }

    public async removeUserFile(fileId: string): Promise<void> {
        await this.apiCall<{ message: string }>(`user-files/${fileId}`, {}, 'DELETE');
    }

    public async renameUserFile(fileId: string, newName: string): Promise<void> {
        await this.apiCall<{ message: string }>(`user-files/${fileId}/rename`, { name: newName }, 'PUT');
    }

    public async saveUserFileContent(fileId: string, content: string): Promise<void> {
        await this.apiCall<{ message: string }>(`user-files/${fileId}/content`, { content }, 'PUT');
    }

    public async generateNewCorp(prompt: string): Promise<CorpSet> {
        return await this.apiCall<CorpSet>("generate_new_corp", { settings: this.settings, prompt: prompt });
    }

    public async listServers(): Promise<string[]> {
        return await this.apiCall<string[]>("server/list");
    }

    public async switchServer(server: string): Promise<string> {
        return await this.apiCall<string>("server/switch", { server });
    }

    public async handleNaturalLangQuery(
        naturalLangQuery: string
    ): Promise<{ query: string, problems: string }> {
        try {
            const result = await this.apiCall<{ result: { query: string, problems: string } }>(
                'handle-natural-lang-query',
                {
                    settings: this.settings,
                    naturalLangQuery
                }
            );

            return result.result;
        } catch (error) {
            console.error('Error in ProcessorService.handleNaturalLangQuery:', error);
            throw new Error('Failed to generate structured output');
        }
    }

    public async getConfigData(): Promise<string> {
        const response = await this.apiCall<string>(
            'get-config-data',
            {}
        );
        console.log('[ProcessorService] getConfigData response received (length:', response?.length || 0, ')');
        return response;
    }

    public async getDefaultConfigData(): Promise<string> {
        return await this.apiCall<string>(
            'get-default-config-data',
            {}
        );
    }

    public async saveConfigData(configData: string): Promise<void> {
        await this.apiCall<{ message: string }>(
            'save-config-data',
            { configData },
            'POST'
        );
    }

    public async handleAttachment(fileName: string, fileBytes: string, task: string, purpose: string): Promise<{ task_id: string }> {
        return await this.apiCall<{ task_id: string }>(
            'handle_pdf',
            { settings: this.settings, fileName: fileName, fileBytes: fileBytes, task: task, purpose: purpose },
            'POST'
        );
    }

    public async streamUpload(file: File, purpose: string, docType: string): Promise<{ task_id: string }> {
        if (!this.apiUrl) {
            await this.init();
        }

        const formData = new FormData();
        formData.append('file', file);
        formData.append('fileName', file.name);
        formData.append('purpose', purpose);
        formData.append('mimeType', file.type);  // The actual MIME type of the file
        formData.append('docType', docType);     // The business-level document type
        formData.append('token', this.token);    // Still include token in form data for backward compatibility

        // Add audioConnKey for server-side processing
        const audioConnKey = this.gameService.getAudioConnKey();
        if (audioConnKey) {
            formData.append('connId', audioConnKey);
        }
        if (this.settings) formData.append('settings', JSON.stringify(this.settings));

        // Use the base URL without exposing the token in the URL
        const url = `${this.apiUrl}/stream-upload`;
        console.log(`[ProcessorService] streamUpload to ${url} with token in Authorization header (length: ${this.token.length})`);

        const response = await fetch(url, {
            method: 'POST',
            body: formData,
            headers: {
                'Authorization': `Bearer ${this.token}`
            }
        });

        if (!response.ok) {
            let errorMessage = `Upload failed: ${response.statusText}`;
            try {
                const errorData = await response.json();
                errorMessage = errorData.error || errorData.message || errorMessage;
            } catch (e) {
                // If we can't parse the error JSON, use the status text
            }
            console.error('Upload failed:', errorMessage);
            throw new Error(errorMessage);
        }

        const result = await response.json();
        console.log('[ProcessorService] Upload successful, task_id:', result.task_id);
        return result;
    }

    public async pdfTaskStatus(task_id: string): Promise<{ finished: boolean, error: boolean, status: string }> {
        return await this.apiCall<{ finished: boolean, error: boolean, status: string }>(
            'handle_pdf_status',
            { settings: this.settings, task_id: task_id },
            'POST'
        );
    }

    public async pairTablet(code: string): Promise<{ message: string }> {
        return await this.apiCall<{ message: string }>(
            'pair',
            { settings: this.settings, code: code },
            'POST'
        );
    }

    public static getServerHostPort(): string {
        const hostname = window.location.hostname;
        const isLocalDev = hostname === 'localhost' ||
            hostname === '127.0.0.1' ||
            hostname === '::1';

        const port = isLocalDev ? ':3003' :
            window.location.port ? `:${window.location.port}` : '';

        return `${hostname}${port}`;
    }
}
